import type { ReactNode } from "react";
import type { RouteRecord } from "vite-react-ssg";
import { Layout } from "./components/Layout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import AppShell from "./components/AppShell";
import AdminRoute from "./components/AdminRoute";
import AdminLayout from "./components/AdminLayout";
import Seo from "./seo/Seo";
import { buildFaqPage, buildOrganization } from "./seo/jsonld";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import About from "./pages/About";
import Privacy from "./pages/Privacy";
import Contact from "./pages/Contact";
import Orders from "./pages/Orders";
import PersonalizarProducto from "./pages/PersonalizarProducto";
import Cart from "./pages/Cart";
import NotFound from "./pages/NotFound";
import HybridCheckout from "./pages/HybridCheckout";
import Security from "./pages/Security";
import ForgotPassword from "./pages/ForgotPassword";
import PostSale from "./pages/PostSale";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminQuotes from "./pages/admin/AdminQuotes";
import AdminLeads from "./pages/admin/AdminLeads";
import AdminTestimonios from "./pages/admin/AdminTestimonios";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminEstampadosYHojas from "./pages/admin/AdminEstampadosYHojas";
import AdminSecciones from "./pages/admin/AdminSecciones";
import AdminTalleres from "./pages/admin/AdminTalleres";
import AgendaBuilder from "./pages/AgendaBuilder";
import PersonalizaAgenda from "./pages/PersonalizaAgenda";

const homeJsonLd = [buildOrganization(), buildFaqPage()];

const adminElement = (page: ReactNode) => (
  <>
    <Seo robots="noindex,nofollow" />
    <AdminRoute>
      <AdminLayout>{page}</AdminLayout>
    </AdminRoute>
  </>
);

export const routes: RouteRecord[] = [
  {
    element: <AppShell />,
    children: [
      {
        path: "/",
        element: (
          <Layout>
            <Seo path="/" jsonLd={homeJsonLd} />
            <Index />
          </Layout>
        ),
      },
      {
        path: "/login",
        element: (
          <Layout>
            <Seo path="/login" />
            <Login />
          </Layout>
        ),
      },
      {
        path: "/register",
        element: (
          <Layout>
            <Seo path="/register" />
            <Register />
          </Layout>
        ),
      },
      {
        path: "/products",
        element: (
          <Layout>
            <Seo path="/products" />
            <Products />
          </Layout>
        ),
      },
      { path: "/products/:id", element: <Layout><ProductDetail /></Layout> },
      {
        path: "/agenda-builder",
        element: (
          <Layout>
            <Seo path="/agenda-builder" />
            <AgendaBuilder />
          </Layout>
        ),
      },
      {
        path: "/personaliza-agenda",
        element: (
          <Layout>
            <Seo path="/personaliza-agenda" />
            <PersonalizaAgenda />
          </Layout>
        ),
      },
      {
        path: "/personalizar/:productSlug",
        element: <Layout><PersonalizarProducto /></Layout>,
      },
      {
        path: "/about",
        element: (
          <Layout>
            <Seo path="/about" />
            <About />
          </Layout>
        ),
      },
      {
        path: "/privacidad",
        element: (
          <Layout>
            <Seo path="/privacidad" />
            <Privacy />
          </Layout>
        ),
      },
      {
        path: "/contact",
        element: (
          <Layout>
            <Seo path="/contact" />
            <Contact />
          </Layout>
        ),
      },
      {
        path: "/orders",
        element: (
          <Layout>
            <Seo path="/orders" />
            <ProtectedRoute>
              <Orders />
            </ProtectedRoute>
          </Layout>
        ),
      },
      {
        path: "/security",
        element: (
          <Layout>
            <Seo path="/security" />
            <ProtectedRoute>
              <Security />
            </ProtectedRoute>
          </Layout>
        ),
      },
      {
        path: "/cart",
        element: (
          <Layout>
            <Seo path="/cart" />
            <Cart />
          </Layout>
        ),
      },
      {
        path: "/checkout",
        element: (
          <Layout>
            <Seo path="/checkout" />
            <HybridCheckout />
          </Layout>
        ),
      },
      {
        path: "/forgot-password",
        element: (
          <>
            <Seo path="/forgot-password" />
            <ForgotPassword />
          </>
        ),
      },
      {
        path: "/postventa/:token",
        element: (
          <Layout>
            <Seo robots="noindex,nofollow" />
            <PostSale />
          </Layout>
        ),
      },
      { path: "/admin", element: adminElement(<AdminDashboard />) },
      { path: "/admin/products", element: adminElement(<AdminProducts />) },
      { path: "/admin/orders", element: adminElement(<AdminOrders />) },
      { path: "/admin/quotes", element: adminElement(<AdminQuotes />) },
      { path: "/admin/leads", element: adminElement(<AdminLeads />) },
      {
        path: "/admin/testimonios",
        element: adminElement(<AdminTestimonios />),
      },
      { path: "/admin/users", element: adminElement(<AdminUsers />) },
      { path: "/admin/settings", element: adminElement(<AdminSettings />) },
      {
        path: "/admin/estampados-y-hojas",
        element: adminElement(<AdminEstampadosYHojas />),
      },
      {
        path: "/admin/secciones-agenda",
        element: adminElement(<AdminSecciones />),
      },
      { path: "/admin/talleres", element: adminElement(<AdminTalleres />) },
      {
        path: "/404",
        element: (
          <Layout>
            <Seo robots="noindex,nofollow" />
            <NotFound />
          </Layout>
        ),
      },
      {
        path: "*",
        element: (
          <Layout>
            <Seo robots="noindex,nofollow" />
            <NotFound />
          </Layout>
        ),
      },
    ],
  },
];

export default routes;
